import React, { useMemo, useState } from 'react';

import {
    TASK_STATUS_FILTERS,
    taskMatchesStatusFilter,
} from '../helpers';
import TaskListPagination from './TaskListPagination';

const TaskSection = ({
    title,
    description,
    items,
    emptyMessage,
    framed = false,
    renderTask,
    showStatusFilter = true,
    layout = 'list',
    serverSideFilter = false,
    search = '',
    onSearchChange,
    searchPlaceholder = 'Search by Task ID, title, description, or assignee…',
    statusFilter: controlledStatusFilter,
    onStatusFilterChange,
    pagination,
    onPageChange,
    loading = false,
    error = '',
}) => {
    const [internalStatusFilter, setInternalStatusFilter] = useState('all');
    const statusFilter = controlledStatusFilter ?? internalStatusFilter;
    const setStatusFilter = onStatusFilterChange ?? setInternalStatusFilter;

    const filteredItems = useMemo(() => {
        if (serverSideFilter) {
            return items;
        }
        return items.filter((t) => taskMatchesStatusFilter(t, statusFilter));
    }, [items, statusFilter, serverSideFilter]);

    const totalCount = serverSideFilter
        ? pagination?.total ?? items.length
        : items.length;

    const showSearch = typeof onSearchChange === 'function';

    return (
        <section
            className={`${
                framed
                    ? 'rounded-2xl border border-zinc-200 bg-white p-5 shadow-xl backdrop-blur-xl dark:border-zinc-800 dark:bg-zinc-900/40 sm:p-6'
                    : ''
            } space-y-4`}
        >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-1">
                    <h2 className="text-lg font-semibold tracking-tight text-zinc-900 dark:text-white sm:text-xl">
                        {title}
                    </h2>
                    <p className="text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                        {description}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2 self-start sm:flex-col sm:items-end sm:gap-1">
                    <div className="rounded-lg border border-zinc-200 bg-zinc-100 px-3 py-1.5 text-xs font-medium tabular-nums text-zinc-700 dark:border-zinc-800 dark:bg-zinc-950/60 dark:text-zinc-300">
                        {filteredItems.length} task{filteredItems.length === 1 ? '' : 's'}
                        {showStatusFilter &&
                        statusFilter !== 'all' &&
                        !serverSideFilter &&
                        items.length
                            ? ` · of ${items.length}`
                            : ''}
                        {serverSideFilter && totalCount > filteredItems.length
                            ? ` · page of ${totalCount}`
                            : ''}
                    </div>
                </div>
            </div>

            {showSearch ? (
                <div className="relative">
                    <i
                        className="fa-solid fa-magnifying-glass pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-zinc-400"
                        aria-hidden
                    />
                    <input
                        type="search"
                        value={search}
                        onChange={(event) => onSearchChange(event.target.value)}
                        placeholder={searchPlaceholder}
                        className="w-full rounded-xl border border-zinc-200 bg-white py-2.5 pl-9 pr-3 text-sm text-zinc-900 outline-none transition-shadow placeholder:text-zinc-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 dark:border-zinc-700 dark:bg-zinc-950/50 dark:text-zinc-100 dark:placeholder:text-zinc-500"
                        aria-label="Search tasks by Task ID, title, description, or assignee"
                    />
                </div>
            ) : null}

            {error ? (
                <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
                    {error}
                </p>
            ) : null}

            {showStatusFilter && (items.length > 0 || serverSideFilter) ? (
                <div className="flex flex-wrap gap-1.5">
                    {TASK_STATUS_FILTERS.map((f) => {
                        const active = statusFilter === f.id;
                        return (
                            <button
                                key={f.id}
                                type="button"
                                onClick={() => setStatusFilter(f.id)}
                                disabled={loading}
                                className={`rounded-lg border px-2.5 py-1 text-[11px] font-semibold transition-colors disabled:opacity-50 ${
                                    active
                                        ? 'border-teal-500 bg-teal-50 text-teal-900 dark:border-teal-600 dark:bg-teal-950/50 dark:text-teal-200'
                                        : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300 dark:border-zinc-700 dark:bg-zinc-900/40 dark:text-zinc-400 dark:hover:border-zinc-600'
                                }`}
                            >
                                {f.label}
                            </button>
                        );
                    })}
                </div>
            ) : null}

            {loading && !filteredItems.length ? (
                <div className="flex items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-zinc-50/80 py-10 dark:border-zinc-800 dark:bg-zinc-950/40">
                    <i className="fa-solid fa-spinner fa-spin text-teal-600 dark:text-teal-400" aria-hidden />
                    <span className="text-sm text-zinc-600 dark:text-zinc-400">Loading tasks…</span>
                </div>
            ) : items.length || serverSideFilter ? (
                filteredItems.length ? (
                    <div className="space-y-4">
                        <div
                            className={
                                layout === 'grid'
                                    ? 'grid grid-cols-1 gap-3 sm:grid-cols-2'
                                    : 'space-y-2'
                            }
                        >
                            {filteredItems.map((item) => (
                                <React.Fragment key={item.id}>
                                    {renderTask(item)}
                                </React.Fragment>
                            ))}
                        </div>
                        {serverSideFilter && onPageChange ? (
                            <TaskListPagination
                                pagination={pagination}
                                onPageChange={onPageChange}
                                loading={loading}
                            />
                        ) : null}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-zinc-300 bg-zinc-50/50 px-4 py-8 text-center dark:border-zinc-700 dark:bg-zinc-950/30">
                        <i className="fa-solid fa-filter text-zinc-400 dark:text-zinc-600" />
                        <p className="max-w-xs text-sm text-zinc-600 dark:text-zinc-400">
                            {search.trim()
                                ? 'No tasks match your search. Try a Task ID (e.g. 27 or #27), title, or assignee.'
                                : 'No tasks match this status filter. Choose "All" or another status.'}
                        </p>
                    </div>
                )
            ) : (
                <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-zinc-300 bg-zinc-50/50 px-4 py-10 text-center dark:border-zinc-700 dark:bg-zinc-950/30">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
                        <i className="fa-regular fa-folder-open text-zinc-400 dark:text-zinc-500" />
                    </div>
                    <p className="max-w-xs text-sm text-zinc-600 dark:text-zinc-400">{emptyMessage}</p>
                </div>
            )}
        </section>
    );
};

export default TaskSection;
